import {
  Box,
  Chip,
  TextField,
} from "@mui/material";
import React, { useState, useMemo } from "react";
import ReusableDataTable from "../../Component/DataTable/ReusableDataTablePrime";
import { FilterMatchMode } from "primereact/api";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../store/CommonSlice";
import { CustomMessage } from "../../const/Spinner";
import { enqueueSnackbar } from "notistack";
import ReusablePrimeButton from "../../Component/Buttons/ReusablePrimeButton";
import AddIcon from "@mui/icons-material/Add";
import {
  getAssetTechniqueById,
  getTechniqueAssetMasterDataList,
  saveAssetTechnique,
} from "../../api/Services/masterServie";
import { getTechniqueAssetColumns } from "../../const/AssertData";
import { TechniqueMaster } from "../../types/assetTypes";
import ReusablePrimeReactDialog from "../../Component/Dialog/ReusablePrimeReactDialog";
import { RootState } from "../../store/Reduxstore";

interface AssetTechniqueProps {
  setPathname: (path: string) => void;
}

const AssetTechnique: React.FC = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<TechniqueMaster[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    rows: 13,
    totalRecords: 0,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [assetTechniqueData, setAssetTechniqueData] = useState<TechniqueMaster>(
    {
      techniqueId: 0,
      techniqueName: "",
    }
  );

  // Memoized column definition
  const columns = useMemo(() => getTechniqueAssetColumns(), []);

  if (columns.filter((val) => val.field === "action").length === 0) {
    columns.push({
      field: "action",
      header: "Action",
      sortable: false,
      filter: false,
      filterField: "action",
      filterPlaceholder: "Search",
      style: { minWidth: "12rem" },
      body: (rowData: any) => (
        <ReusablePrimeButton
          icon="pi pi-pencil"
          className="p-button-rounded p-button-outlined"
          onClick={() => handleEditFamilyAsset(rowData.techniqueId)}
        />
      ),
    });
  }

  const [filterData, setFilterData] = useState<Record<string, any>>(() =>
    (columns ?? []).reduce((acc, col) => {
      if (col.field) {
        acc[col.field] = { value: "", matchMode: "startsWith" }; // Ensure correct structure
      }
      return acc;
    }, {} as Record<string, { value: string; matchMode: string }>),
  );

  const handleEditFamilyAsset = async (techniqueId: number) => {
    dispatch(setLoading(true));
    try {
      const response = await getAssetTechniqueById(techniqueId);
      let { data, status } = response;
      if (status.code === 200 && data) {
        setAssetTechniqueData(data);
        setOpenDialog(true);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Generate filters dynamically

  const onFetchData = async (page: number, rows: number, filter?: Record<string, any>) => {
    dispatch(setLoading(true));
    try {
      const { data, status, pagination } =
        await getTechniqueAssetMasterDataList(page, rows, filter ?? filterData);
      if (status.code === 200 && data?.length > 0) {
        setData(data);
        setPagination({
          page: pagination.currentPage,
          rows: pagination.limit,
          totalRecords: pagination.totalCount,
        });
      } else {
        setData([]);
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setAssetTechniqueData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setOpenDialog(false);
    setAssetTechniqueData({
      techniqueId: 0,
      techniqueName: "",
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();
    if (assetTechniqueData.techniqueName === "") {
      CustomMessage("Add the name", "error", enqueueSnackbar);
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await saveAssetTechnique(assetTechniqueData);
      let { status } = response;
      if (status.code === 200 || status.code === 201) {
        CustomMessage(status.message, "success", enqueueSnackbar);
        handleClear();
        onFetchData(PAGESTART, PAGESEND, filterData);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const userDetails = useSelector((state: RootState) => state.auth)

  return (
    <>
      <div
        // className="mb-0 add-absolute"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          //   top: "-37px",
          //   zIndex: "1",
        }}
      >
        {userDetails.role.some(
          r => r.roleName === "Admin" || r.roleName === "Masters List Write"
        ) && (
            <Chip
              className="btn-black-outlined mb-3"
              label="New"
              color="warning"
              onClick={() => setOpenDialog(true)}
              icon={<AddIcon />}
              variant="outlined"
            />
          )}
      </div>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          borderRadius: 3,
          boxShadow: 3,
          padding: 2,
        }}
      >
        <ReusableDataTable
          data={data}
          columns={columns}
          onFetchData={onFetchData}
          emptyMessage="No data found."
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          selectionMode="multiple"
          rowsPerPageOptions={[5, 10, 25, 50]}
          pagination={pagination}
          setPagination={setPagination}
          filters={filterData}
          setFilters={setFilterData}
          onRowDoubleClick={(rowData) => {
            console.log('Double-click row:', rowData);
            handleEditFamilyAsset(rowData.techniqueId);
          }}
        />
      </Box>
      <ReusablePrimeReactDialog
        header="Add Asset Technique"
        visible={openDialog}
        onHide={handleClear}
      //allowedRoles={["Admin", "Master List Write"]}
      >
        <form
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault(); // 🔒 prevent form submission
            }
          }}
        >
          <h6>Asset Technique Name</h6>
          <TextField
            label=""
            fullWidth
            variant="outlined"
            value={assetTechniqueData.techniqueName}
            name="techniqueName"
            onChange={handleChange}
            sx={{ mt: 0 }}
            required
          />
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: "30px"
          }}>
            <ReusablePrimeButton
              onClick={handleClear}
              severity="contrast"
              title="Cancel"
              type="button"
            >
              Cancel
            </ReusablePrimeButton>
            <ReusablePrimeButton className="ms-1" severity="info" onClick={handleSubmit} allowedRoles={["Admin", "Masters List Write"]}>
              Submit
            </ReusablePrimeButton>
          </div>
        </form>
      </ReusablePrimeReactDialog>
    </>
  );
};

export default AssetTechnique;
